import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eligibleExperiencesQueryOptions, GUIDED_EXPERIENCE_QUERY_KEYS } from '@/api/queries/guided-experiences';
import * as guidedExperiencesApi from '@/api/endpoints/guided-experiences';
import { useAuth } from '@/hooks/use-auth';
import type {
  EligibleTour,
  FeatureToursStatus,
  UseFeatureToursReturn,
} from '../types';

export type { UseFeatureToursReturn } from '../types';

export function useFeatureTours(): UseFeatureToursReturn {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const { data: eligibilityData, isLoading, isError } = useQuery(
    eligibleExperiencesQueryOptions(userId)
  );

  const status: FeatureToursStatus = useMemo(() => {
    if (isLoading) return 'loading';
    if (isError) return 'error';
    return 'ready';
  }, [isLoading, isError]);

  const tours = useMemo<EligibleTour[]>(
    () => eligibilityData?.tours ?? [],
    [eligibilityData]
  );

  const digestTours = useMemo<EligibleTour[]>(
    () => tours.filter((t) => t.triggerType === 'LOGIN_DIGEST' || t.triggerType === 'BOTH'),
    [tours]
  );

  const contextualTours = useMemo<EligibleTour[]>(
    () => tours.filter((t) => t.triggerType === 'CONTEXTUAL' || t.triggerType === 'BOTH'),
    [tours]
  );

  const invalidateEligible = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(userId),
    });
  }, [queryClient, userId]);

  const startTour = useCallback(
    async (slug: string, version: number) => {
      await guidedExperiencesApi.startExperience(slug, version);
      invalidateEligible();
    },
    [invalidateEligible]
  );

  const dismissTour = useCallback(
    async (slug: string) => {
      await guidedExperiencesApi.dismissExperience(slug);
      invalidateEligible();
    },
    [invalidateEligible]
  );

  const completeTour = useCallback(
    async (slug: string) => {
      await guidedExperiencesApi.completeExperience(slug);
      invalidateEligible();
    },
    [invalidateEligible]
  );

  return {
    status,
    tours,
    digestTours,
    contextualTours,
    startTour,
    dismissTour,
    completeTour,
  };
}
